import { getYear, getDay, parseISO, formatISO, isValid, eachDayOfInterval, endOfMonth } from "date-fns";
import React, { cloneElement, PropsWithChildren, ReactElement, useState } from "react";
import styled from "@emotion/styled";
import { getMonth } from "date-fns";

const StyledCalendar = styled.div`
  .calendarNav {
    display: flex;
    font-size: 2em;
    header,
    footer {
      flex: 2 1;
      text-align: center;
    }
    button {
      display: block;
      flex: 1 1;
      text-align: center;
      background: transparent;
      border: none;
    }
  }

  .calendarDay {
    display: block;
    height: 100%;
  }

  .calendarMonths {
    display: grid;
    gap: 1em;
    grid-template-columns: repeat(auto-fit, minmax(250px, 250px));
    align-content: space-evenly;
    justify-content: space-evenly;
  }

  .calendarMonth {
    header {
      font-weight: bold;
      text-align: center;
      padding: 0.25em 0;
    }
    .calendarWeekday {
      font-weight: bold;
      text-align: center;
      padding: 0.25em;
      font-size: 0.65em;
    }
    .calendarDays {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      grid-template-rows: repeat(7, 1fr);
      align-content: space-between;
      justify-content: space-between;
      align-items: center;
      gap: 2px;
    }

    .calendarDay > * {
      text-align: center;
      width: 100%;
      height: 100%;
      padding: 0;
      aspect-ratio: 1/1;
    }
  }
`;

const monthNameFormatter = new Intl.DateTimeFormat(undefined, {
  month: "long",
});
const weekdayNameFormatter = new Intl.DateTimeFormat(undefined, {
  weekday: "short",
});
const materializedWeekdays = [1, 2, 3, 4, 5, 6, 7].map((i) => weekdayNameFormatter.format(new Date(2021, 2, i)));

function materializedMonths(startDate: Date, monthsCount: number) {
  const startYearMonth = new YearMonth(getYear(startDate), getMonth(startDate));
  const months: YearMonth[] = [];

  for (let i = 0; i < monthsCount; i++) {
    months.push(startYearMonth.addMonths(i));
  }

  return months;
}

class YearMonth {
  constructor(
    public year: number,
    public month: number,
  ) {}

  firstDate(): Date {
    return new Date(this.year, this.month, 1);
  }

  addMonths(months: number): YearMonth {
    const resultYear = Math.floor((this.year * 12 + this.month + months) / 12);
    const resultMonth = (this.year * 12 + this.month + months) % 12;

    return new YearMonth(resultYear, resultMonth);
  }

  subMonths(months: number): YearMonth {
    return this.addMonths(months * -1);
  }

  toString(): string {
    return `${this.year}/${this.month + 1}`;
  }
}

export type DateElement = (dateString: string) => ReactElement;

interface CalendarMonthProps {
  month: YearMonth;
  dateElement: DateElement;
}

function CalendarMonth({ month, dateElement }: PropsWithChildren<CalendarMonthProps>) {
  const date = month.firstDate();
  const monthStartsAfter = (getDay(date) + 6) % 7;
  const daysOfMonth = eachDayOfInterval({
    start: date,
    end: endOfMonth(date),
  });
  return (
    <div className="calendarMonth">
      <header>{monthNameFormatter.format(date)}</header>
      <div className="calendarDays">
        {materializedWeekdays.map((weekdayName) => (
          <div key={`weekday-${weekdayName}`} className="calendarWeekday">
            {weekdayName}
          </div>
        ))}
        {Array.from(Array(monthStartsAfter)).map((e, i) => (
          <div key={i} className="calendarDay spacer"></div>
        ))}
        {daysOfMonth.map((day) => {
          const dateString = formatISO(day, { representation: "date" });
          return (
            <time className="calendarDay" dateTime={dateString} key={`day-${dateString}`}>
              {dateElement(dateString)}
            </time>
          );
        })}
      </div>
    </div>
  );
}

interface CalendarNavProps {
  onPrev(): void;
  onNext(): void;
}

function CalendarNav({ onPrev, onNext, children }: PropsWithChildren<CalendarNavProps>) {
  return (
    <nav className="calendarNav">
      <button onClick={onPrev} className="prev" type="button">
        ←
      </button>
      {children}
      <button onClick={onNext} className="next" type="button">
        →
      </button>
    </nav>
  );
}

interface CalendarProps {
  start?: string;
  monthsCount?: number | string;
  dateElement: DateElement;
}

function Calendar({ start, dateElement, monthsCount = 9 }: CalendarProps) {
  const monthsCountNumber: number = Number.isInteger(monthsCount)
    ? (monthsCount as number)
    : parseInt(monthsCount.toString());
  let startDate = start && parseISO(start);
  if (!isValid(startDate)) startDate = new Date();

  const [visibleMonths, setVisibleMonths] = useState<YearMonth[]>(() =>
    materializedMonths(startDate, monthsCountNumber),
  );
  const year = Array.from(new Set(visibleMonths.map((month) => month.year))).join("/");

  const prevMonth = () =>
    setVisibleMonths((visibleMonthsWas) => {
      const repealed = visibleMonthsWas.pop();
      return [repealed.subMonths(monthsCountNumber), ...visibleMonthsWas];
    });
  const nextMonth = () =>
    setVisibleMonths((visibleMonthsWas) => {
      const repealed = visibleMonthsWas.shift();
      return [...visibleMonthsWas, repealed.addMonths(monthsCountNumber)];
    });

  return (
    <StyledCalendar>
      <CalendarNav onNext={nextMonth} onPrev={prevMonth}>
        <header>{year}</header>
      </CalendarNav>
      <div className="calendarMonths">
        {visibleMonths.map((month) => (
          <CalendarMonth key={`month-${month.toString()}`} month={month} dateElement={dateElement}></CalendarMonth>
        ))}
      </div>
      <CalendarNav onNext={nextMonth} onPrev={prevMonth}></CalendarNav>
    </StyledCalendar>
  );
}

export default Calendar;
